import React, { useEffect, useState, useRef } from 'react';
import AppShell from '@/components/AppShell';
import client from '@/lib/api';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Progress } from '@/components/ui/progress';
import { Skeleton } from '@/components/ui/skeleton';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { Loader2, IdCard, CheckCircle2, ScanLine, Sparkles, AlertTriangle } from 'lucide-react';

const FIELDS = [
  { k: 'first_name', l: 'First name', req: true },
  { k: 'middle_name', l: 'Middle name' },
  { k: 'last_name', l: 'Last name', req: true },
  { k: 'phone', l: 'Phone', req: true },
  { k: 'dob', l: 'Date of birth (MM/DD/YYYY)', req: true },
  { k: 'ssn', l: 'SSN (last 4)', req: true },
  { k: 'address', l: 'Street address', req: true },
  { k: 'city', l: 'City', req: true },
  { k: 'state', l: 'State', req: true },
  { k: 'zip', l: 'ZIP', req: true },
  { k: 'license_number', l: 'License number', req: true },
  { k: 'license_state', l: 'License state', req: true },
];

export default function DriverInfo() {
  const [d, setD] = useState(null);
  const [f, setF] = useState({});
  const [saving, setSaving] = useState(false);

  const load = () => client.get('/drivers-me').then((r) => {
    setD(r.data);
    setF({ ...r.data, endorsements: r.data.endorsements || [] });
  }).catch(() => setD(false));
  useEffect(() => { load(); }, []);

  const upd = (k, v) => { setF((p) => ({ ...p, [k]: v })); setOcrUncertain((u) => u.filter((x) => x !== k)); };

  const [ocrType, setOcrType] = useState('CDL');
  const [ocrLoading, setOcrLoading] = useState(false);
  const [ocrUncertain, setOcrUncertain] = useState([]);
  const ocrRef = useRef();
  const runOcr = async (file) => {
    if (!file) return;
    setOcrLoading(true);
    const fd = new FormData(); fd.append('file', file); fd.append('doc_type', ocrType); fd.append('save', 'true');
    try {
      const r = await client.post('/ocr/extract', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      const fields = r.data.fields || {};
      let applied = 0;
      setF((p) => {
        const next = { ...p };
        Object.entries(fields).forEach(([k, v]) => { if (v && String(v).trim()) { next[k] = v; applied += 1; } });
        return next;
      });
      setOcrUncertain(r.data.uncertain_fields || []);
      const labelMap = { CDL: 'CDL', MEDICAL_CARD: 'Medical Card', CLEARINGHOUSE: 'Clearinghouse query' };
      const savedMsg = r.data.saved ? ` It was also saved to your Documents${r.data.expires_at ? ` with an expiry reminder for ${r.data.expires_at}` : ''}.` : '';
      const uncMsg = (r.data.uncertain_fields || []).length ? ` Please double-check the highlighted field(s).` : '';
      toast.success(`Autofilled ${applied} field(s) from your ${labelMap[ocrType] || 'document'}.${savedMsg}${uncMsg}`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Could not read the document'); }
    finally { setOcrLoading(false); if (ocrRef.current) ocrRef.current.value = ''; }
  };

  const save = async () => {
    setSaving(true);
    try {
      const payload = { ...f, license_type: f.license_type || 'CDL A', endorsements: f.endorsements || [] };
      const r = await client.put('/drivers-me', payload);
      setD(r.data);
      toast.success('Driver information saved');
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed to save'); }
    finally { setSaving(false); }
  };

  if (d === null) return <AppShell title="My Driver Information"><Skeleton className="h-96" /></AppShell>;
  if (d === false) return <AppShell title="My Driver Information"><Card className="p-8 text-center text-sm text-muted-foreground">This page is available to driver accounts only.</Card></AppShell>;

  const pct = d.completion?.percent ?? 0;

  return (
    <AppShell title="My Driver Information">
      <div className="mx-auto max-w-3xl space-y-5">
        <Card className={`flex items-center gap-4 p-5 ${d.completion?.complete ? 'border-[hsl(var(--success))]/30 bg-[hsl(var(--success))]/5' : 'border-[hsl(var(--gold))]/40 bg-[hsl(var(--gold))]/5'}`}>
          <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-[hsl(var(--primary))]/10">
            {d.completion?.complete ? <CheckCircle2 className="h-6 w-6 text-[hsl(var(--success))]" /> : <IdCard className="h-6 w-6 text-[hsl(var(--primary))]" />}
          </div>
          <div className="flex-1">
            <div className="font-display font-semibold">Profile completion — {pct}%</div>
            <p className="text-sm text-muted-foreground">{d.completion?.complete ? 'All required details are complete. Thank you!' : 'Complete your details so your administrator can finish onboarding.'}</p>
            <Progress value={pct} className="mt-2 h-2" data-testid="driver-info-progress" />
          </div>
        </Card>

        <Card className="border-[hsl(var(--primary))]/25 bg-[hsl(var(--primary))]/5 p-5" data-testid="ocr-autofill-card">
          <div className="flex items-start gap-3">
            <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-[hsl(var(--primary))]/12"><Sparkles className="h-6 w-6 text-[hsl(var(--primary))]" /></div>
            <div className="flex-1">
              <div className="font-display font-semibold">Autofill from a document</div>
              <p className="text-sm text-muted-foreground">Upload a clear photo or PDF of your CDL or Medical Card — we'll read the details to fill this form and save the file to your Documents with an expiry reminder.</p>
              <div className="mt-3 flex flex-wrap items-center gap-2">
                <Select value={ocrType} onValueChange={setOcrType}>
                  <SelectTrigger className="w-48" data-testid="ocr-doc-type"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="CDL">Commercial Driver's License</SelectItem>
                    <SelectItem value="MEDICAL_CARD">DOT Medical Card</SelectItem>
                    <SelectItem value="CLEARINGHOUSE">Clearinghouse Query</SelectItem>
                  </SelectContent>
                </Select>
                <Button variant="outline" onClick={() => ocrRef.current?.click()} disabled={ocrLoading} data-testid="ocr-upload-button">
                  {ocrLoading ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <ScanLine className="mr-1.5 h-4 w-4" />}{ocrLoading ? 'Reading…' : 'Scan & Autofill'}
                </Button>
                <input ref={ocrRef} type="file" accept=".png,.jpg,.jpeg,.webp,.pdf" className="hidden" onChange={(e) => runOcr(e.target.files[0])} data-testid="ocr-file-input" />
              </div>
            </div>
          </div>
        </Card>

        <Card className="p-6">
          <h3 className="mb-4 font-display text-lg font-semibold">Personal & License Details</h3>
          <div className="grid gap-4 sm:grid-cols-2">
            {FIELDS.map((fld) => {
              const unsure = ocrUncertain.includes(fld.k);
              return (
              <div key={fld.k}>
                <Label className="flex items-center gap-1.5">{fld.l}{fld.req && <span className="text-[hsl(var(--danger))]"> *</span>}
                  {unsure && <span className="inline-flex items-center gap-0.5 rounded bg-[hsl(var(--warning))]/15 px-1.5 py-0.5 text-[10px] font-medium text-[hsl(var(--warning-foreground))]"><AlertTriangle className="h-3 w-3" />Double-check</span>}
                </Label>
                <Input value={f[fld.k] || ''} onChange={(e) => upd(fld.k, e.target.value)}
                  className={`mt-1 ${unsure ? 'border-[hsl(var(--warning))] ring-1 ring-[hsl(var(--warning))]/40' : ''}`}
                  data-testid={`driver-info-${fld.k}`} />
              </div>
              );
            })}
            <div>
              <Label>License type</Label>
              <Select value={f.license_type || 'CDL A'} onValueChange={(v) => upd('license_type', v)}>
                <SelectTrigger className="mt-1" data-testid="driver-info-license_type"><SelectValue /></SelectTrigger>
                <SelectContent>{['CDL A', 'CDL B', 'CDL C', 'Non-CDL'].map((x) => <SelectItem key={x} value={x}>{x}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          <div className="mt-6 flex justify-end">
            <Button onClick={save} disabled={saving} data-testid="driver-info-save-button">{saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}Save Information</Button>
          </div>
        </Card>
      </div>
    </AppShell>
  );
}
